import { ApiRetailerPaymentType, ApiTransaction } from '@vend/types'
import { fetchApi, trackUserEvent } from '@vend/utilities'
import { extractData } from '../../util/extract-data'
import { $http, $resource } from '../../ng-compat/imports'
import {
  checkPaymentTypeAvailability,
  PaymentTypeAvailabilityResponse,
  PaymentTypeAvailabilityResult,
} from '../../queries/payments'
import { client } from '../../apollo'
import { getFeatureFromCache } from '../../util/feature-cache'
import { WEB_REGISTER } from '../web-register.constant'

const API_ENDPOINT = '/api/2.0'
const BASE_REGISTERS_END_POINT = API_ENDPOINT + '/registers'
const REGISTERS_END_POINT =
  BASE_REGISTERS_END_POINT + '/:registerId/transactions'
const PAIRINGS_ENDPOINT =
  API_ENDPOINT + '/payment_types/:retailerPaymentTypeId/pairings'

/**
 * Service class for payment related resources.
 *
 * @class PaymentsResourceService
 */
export class PaymentsResourceService {
  private _pairingsResource: $TSFixMe
  private _registerPaymentsResource: $TSFixMe
  constructor() {
    this._registerPaymentsResource = $resource(
      REGISTERS_END_POINT,
      {
        registerId: '@registerId',
        transactionId: '@transactionId',
        retailerPaymentTypeId: '@retailerPaymentTypeId',
      },
      {
        processPayment: {
          method: 'PUT',
          url: `${REGISTERS_END_POINT}/:retailerPaymentTypeId/payment/:transactionId`,
        },
        processReversal: {
          method: 'PUT',
          url: `${REGISTERS_END_POINT}/:retailerPaymentTypeId/reverse/:transactionId`,
        },
        processRefund: {
          method: 'POST',
          url: `${REGISTERS_END_POINT}/:retailerPaymentTypeId/refund/:transactionId`,
        },
        getTransaction: {
          method: 'GET',
          url: `${REGISTERS_END_POINT}/:transactionId`,
        },
      }
    )

    this._pairingsResource = $resource(
      PAIRINGS_ENDPOINT,
      {
        retailerPaymentTypeId: '@retailerPaymentTypeId',
      },
      {
        getPairings: {
          method: 'GET',
          url: `${PAIRINGS_ENDPOINT}`,
          isArray: true,
        },
      }
    )
  }

  /**
   * Initiates a new register payment.
   *
   * @method processPayment
   *
   * @param {Object} params
   *
   *        @param {String} params.registerId
   *               The register id to associate the payment with.
   *
   *        @param {String} params.transactionId
   *               The unique transactionId for the payment.
   *
   *        @param {String} params.retailerPaymentTypeId
   *               The id for the chosen payment type.
   *
   *        @param {Object} params.data
   *               The data to be passed with the request containing required properties for the payment provider.
   *               The amount field is always required.
   *
   *               @param {Number} params.data.amount
   *                      The payment amount.
   *
   * @return {Promise} Resolved once the payment has been completed.
   */
  public processPayment({
    registerId,
    transactionId,
    retailerPaymentTypeId,
    data,
  }: {
    registerId: string
    transactionId: string
    retailerPaymentTypeId: string
    data: {
      amount: number
    }
  }) {
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'Payment Resource Service Process Payment Request',
      {
        registerId,
        transactionId,
        retailerPaymentTypeId,
        amount: data.amount,
      }
    )

    return this._registerPaymentsResource
      .processPayment(
        { registerId, transactionId, retailerPaymentTypeId },
        data
      )
      .$promise.then((transaction: $TSFixMe) => transaction.toJSON())
  }

  /**
   * Initiates a register payment refund.
   *
   * @method processRefund
   *
   * @param {Object} params
   *
   *        @param {String} params.registerId
   *               The register id to associate the payment with.
   *
   *        @param {String} params.transactionId
   *               The unique transactionId for the payment.
   *
   *        @param {String} params.retailerPaymentTypeId
   *               The id for the chosen payment type.
   *
   *        @param {Object} params.data
   *               The data to be passed with the request containing required properties for the payment provider.
   *               The amount field is always required.
   *
   *               @param {Number} params.data.amount
   *                      The payment amount.
   *
   *               @param {String} [params.data.originalRegisterSalePaymentId]
   *                      The id of the payment in the original sale.
   *
   * @return {Promise} Resolved once the payment has been completed.
   */
  public processRefund({
    registerId,
    transactionId,
    retailerPaymentTypeId,
    data,
  }: {
    registerId: string
    transactionId: string
    retailerPaymentTypeId: string
    data: {
      amount: number
      originalRegisterSalePaymentId?: string
    }
  }) {
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'Payment Resource Service Process Refund Request',
      {
        registerId,
        transactionId,
        retailerPaymentTypeId,
        amount: data.amount,
        originalRegisterSalePaymentId: data.originalRegisterSalePaymentId,
      }
    )

    return this._registerPaymentsResource
      .processRefund({ registerId, transactionId, retailerPaymentTypeId }, data)
      .$promise.then((transaction: $TSFixMe) => transaction.toJSON())
  }

  /**
   * Reverse an existing payment transaction.
   *
   * @method processPaymentReversal
   *
   * @param {Object} params
   *
   *        @param {String} params.registerId
   *               The register id to associate the payment with.
   *
   *        @param {String} params.transactionId
   *               The unique transactionId for the payment.
   *
   *        @param {String} params.retailerPaymentTypeId
   *               The id for the chosen payment type.
   *
   *        @param {Object} params.data
   *               The data to be passed with the request containing required properties for the payment provider.
   *               The amount field is always required.
   *
   *               @param {Number} params.data.amount
   *                      The payment amount.
   *
   * @return {Promise} Resolved once the payment has been reversed.
   */
  public processPaymentReversal({
    registerId,
    transactionId,
    retailerPaymentTypeId,
    data,
  }: {
    registerId: string
    transactionId: string
    retailerPaymentTypeId: string
    data: {
      amount: number
    }
  }) {
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'Payment Resource Service Process Reversal Request',
      {
        registerId,
        transactionId,
        retailerPaymentTypeId,
        amount: data.amount,
      }
    )

    return this._registerPaymentsResource
      .processReversal(
        { registerId, transactionId, retailerPaymentTypeId },
        data
      )
      .$promise.then((transaction: $TSFixMe) => transaction.toJSON())
  }

  /**
   * Retrieve a previous payment transaction.
   *
   * @method getTransaction
   *
   * @param {String} transactionId
   *        The transaction id for the transaction.
   *
   * @return {Promise} Resolved once the transaction has been retrieved.
   */
  public getTransaction({
    registerId,
    transactionId,
  }: {
    registerId: string
    transactionId: string
  }) {
    return this._registerPaymentsResource
      .getTransaction({ registerId, transactionId })
      .$promise.then((transaction: $TSFixMe) => transaction.toJSON())
  }

  /**
   * Retrieve a payment transactions.
   * @deprecated Should use getTransactions instead. Which doesn't rely on $http from Angular
   * @method getTransactions
   *
   * @param {Array} registerSalePaymentIds
   *        The register sale payment id for the sale.
   *
   * @return {Promise} Resolved once the transactions has been retrieved.
   */
  public getTransactions({
    registerId,
    registerSalePaymentIds,
  }: {
    registerId: string
    registerSalePaymentIds: string[]
  }) {
    return $http
      .get<$TSFixMe>(
        `${BASE_REGISTERS_END_POINT}/${registerId}/transactions?register_sale_payment_id=${registerSalePaymentIds.join(
          '&register_sale_payment_id='
        )}`
      )
      .then(extractData)
  }

  /**
   * Retrieve a list of pairings for a retailer payment type.
   *
   * @param {string} retailerPaymentTypeId
   *         The retailer payment type we want to get pairings for
   *
   * @return {Promise} Resolved once the pairings have been retrieved
   */
  public getPairings(retailerPaymentTypeId: string) {
    return this._pairingsResource.getPairings({ retailerPaymentTypeId })
      .$promise
  }
}

/**
 * Retrieve a payment transactions.
 * @method getTransactions
 *
 * @param registerSalePaymentIds
 *        The register sale payment id for the sale.
 *
 * @return {Promise} Resolved once the transactions has been retrieved.
 */
export const getTransactions = (
  registerID: string,
  registerSalePaymentIds: string[]
): Promise<ApiTransaction[]> => {
  return fetchApi(
    `${BASE_REGISTERS_END_POINT}/${registerID}/transactions?register_sale_payment_id=${registerSalePaymentIds.join(
      '&register_sale_payment_id='
    )}`
  )
    .then(resp => {
      if (resp.ok) {
        return resp.json()
      }
      return Promise.reject(new Error('Failed to fetch transactions'))
    })
    .then(({ data }: { data: ApiTransaction[] }) => data)
}

/**
 * Handle a successful check payment type availability
 * @param response {PaymentTypeAvailabilityResponse}
 *        The data or error message returned by the server
 * @returns {boolean} A promise resolved once the payment type availability has been checked
 */
const handleCheckPaymentTypeAvailabilitySuccess = (
  response: PaymentTypeAvailabilityResponse
) => {
  if (!response.data || (response.errors && response.errors.length > 0)) {
    return Promise.reject(new Error())
  }
  return Promise.resolve(response.data.paymentTypeAvailability)
}

/**
 * Get the payment type availability during db maintainence.
 */
const retrievePaymentTypeAvailability = (paymentTypeId: number) => {
  return client
    .query<PaymentTypeAvailabilityResult>({
      query: checkPaymentTypeAvailability,
      variables: { typeId: paymentTypeId },
    })
    .then(response => handleCheckPaymentTypeAvailabilitySuccess(response))
    .catch(() => Promise.reject(new Error()))
}

/**
 * If the swiper_check_payment_type_available_jit FF is enabled retrieve paymentType availability
 */
export const checkSwiperMaintenance = async (
  paymentTypeId: ApiRetailerPaymentType['type_id']
) => {
  if (getFeatureFromCache('swiper_check_payment_type_available_jit')) {
    try {
      const isPaymentTypeAvailable = await retrievePaymentTypeAvailability(
        paymentTypeId
      )
      return !isPaymentTypeAvailable
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        'PaymentCtrl: Failed to check payment type availability during db maintenance'
      )
      // If the payment type availability check failed, proceed with the usual payment process
      return false
    }
  } else {
    return false
  }
}
